import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import StarIcon from '@material-ui/icons/Star';
import Edit from '@material-ui/icons/Edit';
import { useNavigate } from 'react-router-dom';

import {
  GenericStatus,
  LeaderboardConfigFragment,
  LeaderboardConfigInput,
  UserSegmentFragment,
} from 'api';
import { PaddedPaper, ValidatedSwitch } from 'components';

import LeaderboardView from './View';

interface IProps {
  config: LeaderboardConfigFragment;
  segments: UserSegmentFragment[];
  working: boolean;
}

const Leaderboard: FC<IProps> = ({ config, segments }) => {
  const { t } = useTranslation();
  const formik = useFormikContext<LeaderboardConfigInput>();
  const navigate = useNavigate();

  const { errors, setFieldValue, submitForm } = formik;
  const { leaderboard_status, segment_ids } = formik.values;
  const { all_users } = config;

  const isActive = leaderboard_status === GenericStatus.Active;

  function checkedFn(value: GenericStatus) {
    return value === GenericStatus.Active;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>, value: boolean) {
    const status = value ? GenericStatus.Active : GenericStatus.Disabled;
    setFieldValue('leaderboard_status', status);

    setTimeout(() => {
      void submitForm();
    });
  }

  function renderToggle() {
    const hasSegments = !all_users && !segment_ids.length;
    const hasErrors = !!Object.keys(errors).length || hasSegments;

    return (
      <ValidatedSwitch
        name="leaderboard_status"
        color="primary"
        label={t('common:status')}
        disabled={hasErrors}
        onChange={handleChange}
        checkedFn={checkedFn}
      />
    );
  }

  function renderHeader() {
    const readTitle = <Typography variant="subtitle2">{config.title}</Typography>;

    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <StarIcon color={isActive ? 'primary' : 'disabled'} />
        </Grid>
        <Grid item xs>
          {readTitle}
        </Grid>
      </Grid>
    );
  }

  function onClickEdit() {
    navigate(`/admin/leaderboards/${config.id}`);
  }

  return (
    <PaddedPaper>
      <Grid container alignItems="center" justify="space-between">
        <Grid item xs>
          {renderHeader()}
        </Grid>
        <Grid item>
          <Button onClick={onClickEdit} endIcon={<Edit fontSize="small" />}>
            {t('common:edit')}
          </Button>
        </Grid>
        {renderToggle()}
      </Grid>
      <LeaderboardView segments={segments} config={config} />
    </PaddedPaper>
  );
};

export default Leaderboard;
